import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
    Save,
    X,
    Plus,
    Trash2,
    Camera,
    Upload,
    FileDown,
    ChevronLeft,
    Car,
    User,
    Edit3,
    Shield,
    ReceiptText,
    Sparkles,
    MapPin,
    MailCheck,
    Send,
    Search
} from 'lucide-react';
import Layout from '../components/Layout';
import DatePicker, { registerLocale } from "react-datepicker";
import fr from 'date-fns/locale/fr';
import "react-datepicker/dist/react-datepicker.css";

import { PDFGenerationOverlay } from '../components/PDFGenerationOverlay';
import {
    rapportsAPI,
    sinistresAPI,
    photosAPI,
    BASE_URL,
    assurancesAPI,
    genresAPI,
    marquesAPI,
    modelesAPI,
    carosseriesAPI,
    colorsAPI,
    energiesAPI,
    estadosAPI,
    paramsAPI,
    piecesAPI,
    honorairesAPI
} from '../services/api';
import toast from 'react-hot-toast';
import Plate from '../components/Plate';
import DictationButton from '../components/DictationButton';
import TemplateSuggester from '../components/TemplateSuggester';
import { playSound } from '../utils/SoundPlayer';
import ConfirmModal from '../components/ConfirmModal';

registerLocale('fr', fr);

const RapportForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isEditing = id && id !== 'nouveau' && id !== 'nouvelle';
    const isReadOnly = id && !location.pathname.endsWith('/modifier');
    const sinistreIdParam = searchParams.get('sinistre');

    const [showEmailConfirm, setShowEmailConfirm] = useState(false);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [analysingIA, setAnalysingIA] = useState(false);
    const [sinistre, setSinistre] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [rapportId, setRapportId] = useState(null);
    const [pdfStatus, setPdfStatus] = useState('idle');
    const [numeroRapport, setNumeroRapport] = useState('');

    const getPhotoUrl = (photo) => {
        if (!photo) return '';
        if (photo.file_path && photo.file_path.startsWith('http')) {
            return photo.file_path;
        }
        const path = photo.file_path || `/uploads/photos/${photo.filename}`;
        const cleanPath = path.startsWith('/') ? path : `/${path}`;
        return `${BASE_URL}${cleanPath}`;
    };

    // State for claim search modal
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const [showEditSinistreModal, setShowEditSinistreModal] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [updatingSinistre, setUpdatingSinistre] = useState(false);
    const [quickAdd, setQuickAdd] = useState(null); // { type: 'genre'|'carrosserie'|'color'|'energie'|'estado', value: '' }

    // Add Insurance Quick Modal
    const [showAddAssuranceModal, setShowAddAssuranceModal] = useState(false);
    const [newAssurance, setNewAssurance] = useState({ name: '', code: '', city: '', phone: '', email: '' });
    const [savingAssurance, setSavingAssurance] = useState(false);

    // Reference data states
    const [assurances, setAssurances] = useState([]);
    const [genres, setGenres] = useState([]);
    const [marques, setMarques] = useState([]);
    const [modeles, setModeles] = useState([]); // For specific marque
    const [modelYears, setModelYears] = useState([]);
    const [colors, setColors] = useState([]);
    const [energies, setEnergies] = useState([]);
    const [estados, setEstados] = useState([]);
    const [carosseries, setCarosseries] = useState([]);
    const [systemParams, setSystemParams] = useState({});
    const [availableParts, setAvailableParts] = useState([]);
    const [currentModelId, setCurrentModelId] = useState(null);

    const [form, setForm] = useState({
        sinistre_id: sinistreIdParam || '',
        numero_sinistre: '',
        statut: 'brouillon',
        date_rapport: new Date().toISOString().split('T')[0],
        constatations: '',
        observations: '',
        conclusion: '',
        main_oeuvre: '0,00',
        main_oeuvre_txt: '',
        peinture: '0,00',
        valeur_venale: '0,00',
        vetuste: 0,
        vetuste_montant: '0,00',
        jours_immobilisation: 0,
        nb_photos: 0,
        ia_generated: 0,
        ia_generated_at: null,
        inclure_cachet_signature: 0,
        inclure_cachet_photos: 0,
        email_sent: 0,
        email_sent_at: null
    });

    const [pieces, setPieces] = useState([]);

    // Logic for claim search
    useEffect(() => {
        if (!sinistre && searchQuery.length >= 2) {
            const delayDebounce = setTimeout(async () => {
                setSearching(true);
                try {
                    const response = await sinistresAPI.list({ search: searchQuery });
                    setSearchResults(response.data.data || []);
                } catch (error) {
                    console.error('Search error:', error);
                } finally {
                    setSearching(false);
                }
            }, 500);
            return () => clearTimeout(delayDebounce);
        } else if (searchQuery.length < 2) {
            setSearchResults([]);
        }
    }, [searchQuery, sinistre]);

    const handleOpenModal = () => {
        setSearchQuery('');
        setSearchResults([]);
        setShowModal(true);
    };

    const handleSelectSinistre = (s) => {
        setSinistre(s);
        setForm(prev => ({
            ...prev,
            sinistre_id: s.id,
            numero_sinistre: s.numero_sinistre,
        }));
        setSearchQuery('');
        setSearchResults([]);
        setShowModal(false);
    };

    const handleUnlinkSinistre = () => {
        setSinistre(null);
        setForm(prev => ({
            ...prev,
            sinistre_id: '',
            numero_sinistre: '',
        }));
    };

    const parseMontant = (val) => {
        if (typeof val === 'number') return val;
        if (!val) return 0;
        return parseFloat(val.toString().replace(/\s/g, '').replace(',', '.')) || 0;
    };

    // Helpers formatting
    const formatNumber = useCallback((val) => {
        if (!val) return '0,00';
        return parseFloat(val).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }, []);

    useEffect(() => {
        Promise.all([
            assurancesAPI.list(),
            genresAPI.list(),
            marquesAPI.list(),
            colorsAPI.list(),
            energiesAPI.list(),
            estadosAPI.list(),
            carosseriesAPI.list(),
            paramsAPI.list()
        ]).then(([a, g, m, cl, e, es, c, p]) => {
            setAssurances(a.data.data || []);
            setGenres(g.data.data || []);
            setMarques(m.data.data || []);
            setColors(cl.data.data || []);
            setEnergies(e.data.data || []);
            setEstados(es.data.data || []);
            setCarosseries(c.data.data || []);

            const pMap = {};
            if (p.data.data) {
                p.data.data.forEach(item => { pMap[item.param_key] = item.param_value; });
            }
            setSystemParams(pMap);
        }).catch(err => console.error("Error loading references:", err));
    }, []);

    // Auto-calculate Main d'oeuvre based on days
    useEffect(() => {
        if (!isReadOnly) {
            const days = parseInt(form.jours_immobilisation) || 0;
            if (days > 0) {
                const hours = days * 8;
                const genre = (sinistre?.genre || '').toLowerCase();
                const rateStandard = parseFloat(systemParams.taux_horaire_mo) || 250;
                const rateCamion = parseFloat(systemParams.taux_horaire_mo_camion) || 300;
                const rate = genre === 'camion' ? rateCamion : rateStandard;
                const total = hours * rate;
                setForm(prev => {
                    const currentMo = parseMontant(prev.main_oeuvre);
                    if (currentMo !== total) {
                        return { ...prev, main_oeuvre: formatNumber(total) };
                    }
                    return prev;
                });
            }
        }
    }, [form.jours_immobilisation, sinistre?.genre, isReadOnly, formatNumber, systemParams]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            // If we have a sinistre ID in URL, load it
            if (sinistreIdParam) {
                const res = await sinistresAPI.getById(sinistreIdParam);
                const sinistreData = res.data.data || res.data;
                setSinistre(sinistreData);
                setForm(prev => ({
                    ...prev,
                    sinistre_id: sinistreData.id,
                    numero_sinistre: sinistreData.numero_sinistre
                }));
            }

            // If editing a report, load the report
            if (isEditing) {
                const res = await rapportsAPI.getById(id);
                const r = res.data.data || res.data;
                setRapportId(r.id);
                setNumeroRapport(r.numero_rapport || '');
                setForm({
                    sinistre_id: r.sinistre_id,
                    numero_sinistre: r.numero_sinistre,
                    statut: r.statut,
                    date_rapport: r.date_rapport?.split('T')[0] || '',
                    constatations: r.constatations || '',
                    observations: r.observations || '',
                    conclusion: r.conclusion || '',
                    main_oeuvre: formatNumber(r.main_oeuvre),
                    main_oeuvre_txt: r.main_oeuvre_txt || '',
                    peinture: formatNumber(r.peinture),
                    valeur_venale: formatNumber(r.valeur_venale),
                    vetuste: r.vetuste || 0,
                    vetuste_montant: formatNumber(r.vetuste_montant),
                    jours_immobilisation: r.jours_immobilisation || 0,
                    nb_photos: r.nb_photos || 0,
                    ia_generated: r.ia_generated || 0,
                    ia_generated_at: r.ia_generated_at || null,
                    inclure_cachet_signature: r.inclure_cachet_signature || 0,
                    inclure_cachet_photos: r.inclure_cachet_photos || 0,
                    email_sent: r.email_sent || 0,
                    email_sent_at: r.email_sent_at || null
                });
                setPieces(r.pieces || []);
                // Important: The backend sends photos including those from sinistre. 
                // We only store rapport-specific photos in the photos state to avoid duplication with sinistre.
                setPhotos(r.photos || []);

                // Load linked sinistre details if not already loaded
                if (r.sinistre_id && !sinistreIdParam) {
                    const sRes = await sinistresAPI.getById(r.sinistre_id);
                    setSinistre(sRes.data.data || sRes.data);
                }
            }
        } catch (error) {
            toast.error('Erreur lors du chargement des données');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [formatNumber, id, isEditing, sinistreIdParam]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Logic to find currentModelId based on Vehicle info
    useEffect(() => {
        const findModelId = async () => {
            if (!sinistre || !marques.length) return;

            try {
                const brand = marques.find(m => m.name?.toUpperCase() === sinistre.marque_vehicule?.toUpperCase());
                if (!brand) return;

                const res = await modelesAPI.list({
                    brand_id: brand.id,
                    name: sinistre.modele_vehicule,
                    year: sinistre.annee_vehicule
                });

                if (res.data.data && res.data.data.length > 0) {
                    setCurrentModelId(res.data.data[0].id);
                } else {
                    setCurrentModelId(null);
                }
            } catch (err) {
                console.error("Error matching model:", err);
            }
        };
        findModelId();
    }, [sinistre, marques]);

    // Logic to load available parts (Wider search: same brand & model name across all years)
    useEffect(() => {
        const fetchParts = async () => {
            if (!sinistre || !marques.length) {
                setAvailableParts([]);
                return;
            }

            try {
                const brand = marques.find(m => m.name?.toUpperCase() === sinistre.marque_vehicule?.toUpperCase());
                if (!brand) return;

                // We fetch all parts for this model name, ignoring the specific year to allow compatibility suggestions
                const res = await piecesAPI.list({
                    brand_id: brand.id,
                    model_name: sinistre.modele_vehicule
                });
                setAvailableParts(res.data.data || []);
            } catch (err) {
                console.error("Error fetching available parts:", err);
            }
        };
        fetchParts();
    }, [sinistre, marques]);



    const handleBlur = (field) => {
        const val = form[field];
        setForm(prev => ({ ...prev, [field]: formatNumber(parseMontant(val)) }));
    };

    const handlePieceBlur = (idx, field) => {
        const val = pieces[idx][field];
        const updated = [...pieces];
        updated[idx][field] = formatNumber(parseMontant(val));
        updated[idx].sous_total = updated[idx].quantite * parseMontant(updated[idx][field]);
        setPieces(updated);
    };

    // Pieces management
    const addPiece = () => {
        if (!sinistre?.marque_vehicule?.trim() || !sinistre?.modele_vehicule?.trim()) {
            toast.error(
                "Le Modèle et la Marque du véhicule doivent être spécifiés dans le dossier sinistre pour ajouter des pièces de rechange.",
                { duration: 5000 }
            );
            return;
        }
        setPieces([...pieces, { designation: '', quantite: 1, prix_unitaire: '0,00', category: '', sous_total: 0 }]);
    };

    const removePiece = (idx) => {
        setPieces(prev => Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : []);
    };

    const updatePiece = (idx, field, value) => {
        const updated = [...pieces];
        updated[idx][field] = value;
        if (field === 'quantite' || field === 'prix_unitaire') {
            const q = field === 'quantite' ? parseFloat(value) || 0 : updated[idx].quantite;
            const p = field === 'prix_unitaire' ? parseMontant(value) : parseMontant(updated[idx].prix_unitaire);
            updated[idx].sous_total = q * p;
        }
        setPieces(updated);
    };

    const handleDesignationChange = (idx, val) => {
        const updated = [...pieces];
        updated[idx].designation = val;

        // Auto-fill price and category if it matches a catalog item
        const matching = availableParts.find(ap => ap.name?.toLowerCase() === val?.toLowerCase());
        if (matching) {
            updated[idx].prix_unitaire = formatNumber(matching.price);
            updated[idx].category = matching.category || '';
            updated[idx].sous_total = updated[idx].quantite * matching.price;
        }
        setPieces(updated);
    };

    // Totals calculations
    const totals = useMemo(() => {
        const piecesTotal = pieces.reduce((sum, p) => sum + (parseFloat(p.sous_total) || 0), 0);
        const mo = parseMontant(form.main_oeuvre) || 0;
        const pein = parseMontant(form.peinture) || 0;
        const vetuste_montant = parseMontant(form.vetuste_montant) || 0;
        return {
            pieces: piecesTotal,
            general: Math.max(0, piecesTotal + mo + pein - vetuste_montant)
        };
    }, [pieces, form.main_oeuvre, form.peinture, form.vetuste_montant]);

    const actualPhotoCount = useMemo(() => {
        const sPhotos = sinistre?.photos || [];
        const rPhotos = photos || [];
        const uniqueInRapport = rPhotos.filter(rp => !sPhotos.some(sp => sp.id === rp.id));
        return sPhotos.length + uniqueInRapport.length;
    }, [sinistre?.photos, photos]);

    // Photo management
    const handlePhotoUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const formData = new FormData();
        files.forEach(file => formData.append('photos', file));

        if (rapportId || id) {
            formData.append('rapport_id', rapportId || id);
        } else if (form.sinistre_id) {
            formData.append('sinistre_id', form.sinistre_id.toString().replace(/^(s-|sz-)/, ''));
        } else {
            toast.error("Veuillez lier un dossier au préalable.");
            return;
        }

        if (form.numero_sinistre) formData.append('numero_sinistre', form.numero_sinistre);

        setSearching(true); // Reusing searching state for simple spinner
        try {
            const res = await photosAPI.upload(formData, (progressEvent) => {
                if (progressEvent.total) {
                    setUploadProgress(Math.round(progressEvent.loaded / progressEvent.total * 100));
                }
            });
            setPhotos(prev => [...prev, ...(res.data.data || res.data || [])]);
            toast.success(`${files.length} photo(s) ajoutée(s)`);
            e.target.value = ''; // Reset input to allow submitting same file again
        } catch (error) {
            toast.error('Erreur lors de l\'envoi des photos');
        } finally {
            setSearching(false);
            setUploadProgress(0);
        }
    };

    const removePhoto = async (photo) => {
        try {
            if (photo.id) {
                await photosAPI.delete(photo.id);
            }
            // 1. Remove from rapport photos state
            setPhotos(prev => prev.filter(p => {
                if (photo.id && p.id) return p.id !== photo.id;
                return p.filename !== photo.filename;
            }));
            // 2. ALSO remove from sinistre photos state (in case it came from there but is being removed)
            setSinistre(prev => ({
                ...prev,
                photos: (prev?.photos || []).filter(p => {
                    if (photo.id && p.id) return p.id !== photo.id;
                    return p.filename !== photo.filename;
                })
            }));
            toast.success('Photo supprimée');
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    const removeSinistrePhoto = async (photo) => {
        // Just call the same unified delete function
        return removePhoto(photo);
    };

    const handleEditSinistreOpen = async () => {
        if (!sinistre) return;

        // Ensure we format date correctly for input type="date"
        const formattedSinistre = {
            ...sinistre,
            date_sinistre: sinistre.date_sinistre ? sinistre.date_sinistre.split('T')[0] : ''
        };

        setEditForm(formattedSinistre);

        // If there's a marque, load its models
        if (sinistre.marque_vehicule) {
            try {
                const brand = marques.find(m => m.name && m.name.toUpperCase() === sinistre.marque_vehicule?.toUpperCase());
                if (brand) {
                    const res = await modelesAPI.list({ brand_id: brand.id });
                    setModeles(res.data.data || []);
                }
            } catch (e) {
                console.error(e);
            }
        }

        setShowEditSinistreModal(true);
    };

    const handleMarqueChangeInModal = async (val) => {
        setEditField('marque_vehicule', val);
        if (!val) {
            setModeles([]);
            setEditField('marque_logo', null);
            return;
        }

        const brand = marques.find(m => m.name && m.name.toUpperCase() === val.toUpperCase());
        if (brand) {
            setEditField('marque_logo', brand.logo);
            try {
                const res = await modelesAPI.list({ brand_id: brand.id, unique: true });
                setModeles(res.data.data || []);
            } catch (e) {
                console.error(e);
            }
        } else {
            setModeles([]);
            setModelYears([]);
            setEditField('marque_logo', null);
        }
    };

    const handleModeleChangeInModal = (val) => {
        setEditField('modele_vehicule', val);
        if (!val) {
            setModelYears([]);
            return;
        }

        const brand = marques.find(m => m.name && m.name.toUpperCase() === (editForm.marque_vehicule || '').toUpperCase());
        if (brand) {
            modelesAPI.getYears(brand.id, val)
                .then(res => {
                    const years = res.data.data || [];
                    setModelYears(years);
                    if (years.length > 0 && !editForm.annee_vehicule) {
                        setEditField('annee_vehicule', years[0]);
                    }
                })
                .catch(() => setModelYears([]));
        }
    };

    const handleAssuranceSelectInModal = (e) => {
        const name = e.target.value;
        const matching = assurances.filter(a => a && a.name === name);
        if (matching.length >= 1) {
            setEditForm(prev => ({
                ...prev,
                nom_assurance: name,
                code_assurance: matching[0].code || '',
                ville_assurance: matching[0].city || '',
                assurance_logo: matching[0].logo || ''
            }));
        } else {
            setEditForm(prev => ({
                ...prev,
                nom_assurance: name,
                code_assurance: '',
                ville_assurance: '',
                assurance_logo: ''
            }));
        }
    };

    const handleQuickAdd = async () => {
        if (!quickAdd.value.trim()) return;
        try {
            if (quickAdd.type === 'genre') {
                const res = await genresAPI.create({ genre: quickAdd.value });
                const newItem = { id: res.data.data.id, genre: quickAdd.value };
                setGenres(prev => [...prev, newItem]);
                setEditField('genre', quickAdd.value);
            } else if (quickAdd.type === 'carrosserie') {
                await carosseriesAPI.create({ carosserie: quickAdd.value });
                setEditField('carrosserie', quickAdd.value);
            } else if (quickAdd.type === 'color') {
                const res = await colorsAPI.create({ color: quickAdd.value });
                const newItem = { id: res.data.data.id, color: quickAdd.value };
                setColors(prev => [...prev, newItem]);
                setEditField('couleur', quickAdd.value);
            } else if (quickAdd.type === 'energie') {
                const res = await energiesAPI.create({ energie: quickAdd.value });
                const newItem = { id: res.data.data.id, energie: quickAdd.value };
                setEnergies(prev => [...prev, newItem]);
                setEditField('energie', quickAdd.value);
            } else if (quickAdd.type === 'estado') {
                const res = await estadosAPI.create({ estado: quickAdd.value });
                const newItem = { id: res.data.data.id, estado: quickAdd.value };
                setEstados(prev => [...prev, newItem]);
                setEditField('etat_vehicule', quickAdd.value);
            }
            setQuickAdd(null);
            toast.success('Ajouté avec succès');
        } catch {
            toast.error('Erreur lors de l\'ajout');
        }
    };

    const handleSaveNewAssurance = async () => {
        if (!newAssurance.name) {
            toast.error("Le nom de la compagnie est obligatoire");
            return;
        }
        setSavingAssurance(true);
        try {
            const res = await assurancesAPI.create(newAssurance);
            const savedAss = res.data.data || res.data;
            setAssurances(prev => [...prev, savedAss]);

            // Select it in the main edit form
            setEditForm(prev => ({
                ...prev,
                nom_assurance: savedAss.name,
                code_assurance: savedAss.code || '',
                ville_assurance: savedAss.city || ''
            }));

            setShowAddAssuranceModal(false);
            setNewAssurance({ name: '', code: '', city: '', phone: '', email: '' });
            toast.success('Assurance ajoutée avec succès');
        } catch (error) {
            console.error(error);
            toast.error("Erreur lors de l'ajout de la compagnie");
        } finally {
            setSavingAssurance(false);
        }
    };

    const handleEditSinistreSave = async () => {
        setUpdatingSinistre(true);
        try {
            const cleanId = sinistre.id.toString().replace(/^(sz-|s-)/, '');
            await sinistresAPI.update(cleanId, editForm);

            // Re-fetch sinistre to refresh the component's state
            const sRes = await sinistresAPI.getById(cleanId);
            const freshSinistre = sRes.data.data || sRes.data;
            setSinistre(freshSinistre);

            // Also update form link if number changed
            setForm(prev => ({
                ...prev,
                numero_sinistre: freshSinistre.numero_sinistre
            }));

            toast.success('Informations du dossier mises à jour');
            setShowEditSinistreModal(false);
        } catch (error) {
            console.error('Update sinistre error:', error);
            toast.error('Erreur lors de la mise à jour');
        } finally {
            setUpdatingSinistre(false);
        }
    };

    const setEditField = (field, val) => {
        setEditForm(prev => ({ ...prev, [field]: val }));
    };

    const handleSubmit = async (targetStatut = null) => {
        if (saving) return;

        if (!form.sinistre_id) {
            toast.error('Veuillez lier un dossier de sinistre');
            return;
        }

        const payload = {
            ...form,
            statut: targetStatut === 'finalise' ? 'finalise' : (targetStatut || form.statut),
            main_oeuvre: parseMontant(form.main_oeuvre),
            peinture: parseMontant(form.peinture),
            valeur_venale: parseMontant(form.valeur_venale),
            vetuste_montant: parseMontant(form.vetuste_montant),
            pieces: (Array.isArray(pieces) ? pieces : []).map(p => ({
                ...p,
                prix_unitaire: parseMontant(p.prix_unitaire)
            })),
            vetuste: parseInt(form.vetuste) || 0,
            jours_immobilisation: parseInt(form.jours_immobilisation) || 0,
            photos: (Array.isArray(photos) ? photos : []).map(p => p.id || p.filename),
            nb_photos: actualPhotoCount || form.nb_photos || 0,
            inclure_cachet_signature: form.inclure_cachet_signature ? 1 : 0,
            inclure_cachet_photos: form.inclure_cachet_photos ? 1 : 0
        };

        setSaving(true);
        try {
            if (isEditing) {
                await rapportsAPI.update(id, payload);
            } else {
                const res = await rapportsAPI.create(payload);
                const newId = res.data?.data?.id || res.data?.id;
                setRapportId(newId);
                navigate(`/rapports/${newId}/modifier`, { replace: true });
            }

            // Auto-save NEW pieces to the catalog if possible (don't block the UI)
            if (currentModelId && Array.isArray(pieces)) {
                // Avoid redundant concurrent requests if the same part is twice in the list
                const processedLocally = new Set();
                
                pieces.forEach(async (p) => {
                    const cleanName = (p.designation || '').trim();
                    if (!cleanName || processedLocally.has(cleanName.toUpperCase())) return;
                    processedLocally.add(cleanName.toUpperCase());

                    const isExisting = availableParts.some(ap => (ap.name || '').trim().toUpperCase() === cleanName.toUpperCase());
                    
                    if (!isExisting) {
                        try {
                            const res = await piecesAPI.create({
                                name: cleanName,
                                model_id: currentModelId,
                                price: parseMontant(p.prix_unitaire),
                                category: p.category || 'Non spécifié'
                            });
                            
                            // If successfully created OR backend says it was already there, update local reference
                            const data = res.data?.data || res.data;
                            if (data?.id) {
                                setAvailableParts(prev => [...prev, {
                                    id: data.id,
                                    name: cleanName,
                                    price: parseMontant(p.prix_unitaire),
                                    category: p.category || 'Non spécifié'
                                }]);
                            }
                        } catch (err) { }
                    }
                });
            }

            toast.success(isEditing ? 'Rapport mis à jour' : 'Rapport créé avec succès');
            playSound('save');
        } catch (error) {
            const resData = error.response?.data;
            const msg = resData?.message || 'Erreur lors de la sauvegarde';
            
            if (error.response?.status === 400 && resData?.existing_id) {
                toast.error(
                    (t) => (
                        <div>
                            <b>Attention</b><br />
                            <span style={{ fontSize: '13px' }}>{msg}</span><br />
                            <button 
                                className="btn btn-primary btn-sm mt-2"
                                style={{ width: '100%' }}
                                onClick={() => {
                                    toast.dismiss(t.id);
                                    navigate(`/rapports/${resData.existing_id}/modifier`);
                                }}
                            >
                                Ouvrir le PV existant
                            </button>
                        </div>
                    ),
                    { duration: 8000 }
                );
            } else {
                toast.error(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleAnalyseIA = async () => {
        const reportId = id || rapportId;
        if (!reportId) {
            toast.error('Veuillez d\'abord sauvegarder le rapport pour activer l\'analyse IA.');
            return;
        }
        setAnalysingIA(true);
        playSound('ai');
        toast.loading('Analyse IA en cours...', { id: 'ia' });
        try {
            const res = await rapportsAPI.analyseIA(reportId);

            const { constatations, ia_generated, ia_generated_at } = res.data.data;
            setForm(p => ({
                ...p,
                constatations: constatations,
                ia_generated,
                ia_generated_at
            }));
            toast.success('✨ Constatations techniques générées par IA !', { id: 'ia', duration: 5000 });
        } catch (error) {
            const msg = error.response?.data?.message || 'Erreur lors de l\'analyse IA';
            toast.error(msg, { id: 'ia' });
            console.error('[IA]', error);
        } finally {
            setAnalysingIA(false);
        }
    };

    const handleSendEmail = () => {
        const reportId = id || rapportId;
        if (!reportId) {
            toast.error("Veuillez d'abord sauvegarder le rapport");
            return;
        }
        setShowEmailConfirm(true);
    };

    const executeSendEmail = async () => {
        const reportId = id || rapportId;
        setShowEmailConfirm(false);
        const tId = toast.loading("Génération du PDF et envoi au Mandataire...");
        try {
            await rapportsAPI.sendEmail(reportId);
            toast.success("Rapport envoyé avec succès !", { id: tId });
            playSound('save');
            // Refresh local state
            setForm(prev => ({ ...prev, email_sent: 1, email_sent_at: new Date().toISOString() }));
        } catch (error) {
            console.error('[EMAIL_SEND]', error);
            const msg = error.response?.data?.message || "Erreur lors de l'envoi de l'email";
            toast.error(msg, { id: tId });
        }
    };

    const handleGeneratePDF = async () => {
        const reportId = id || rapportId;
        try {
            setPdfStatus('generating');
            const res = await rapportsAPI.generatePDF(reportId);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Rapport_${sinistre?.numero_sinistre || reportId}.pdf`;
            a.click();
            URL.revokeObjectURL(url);

            setPdfStatus('success');
            playSound('pdf');
            setTimeout(() => setPdfStatus('idle'), 1500);
        } catch (error) {
            setPdfStatus('idle');
            console.error('PDF error:', error);
            toast.error('Erreur lors de la génération du PDF', { id: 'pdf' });
        }
    };

    const handleFacturer = async () => {
        const reportId = id || rapportId;
        if (!reportId) return;

        try {
            const res = await honorairesAPI.list({ rapport_id: reportId });
            if (res.data.data && res.data.data.length > 0) {
                navigate(`/honoraires/${res.data.data[0].id}`);
            } else {
                navigate(`/honoraires/nouvelle?rapport=${reportId}`);
            }
        } catch {
            navigate(`/honoraires/nouvelle?rapport=${reportId}`);
        }
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD' }).format(val);
    };

    if (loading) return <Layout><div className="loading">Chargement...</div></Layout>;

    return (
        <Layout>
            <PDFGenerationOverlay status={pdfStatus} />
            <div className="container" style={{ paddingBottom: '40px' }}>
                <div className="flex-between mb-4">
                    <div className="flex-center" style={{ gap: '10px' }}>
                        <button className="btn-icon" onClick={() => navigate(-1)}><ChevronLeft size={20} /></button>
                        <h1 className="h2">
                            {isReadOnly ? `Détails du PV ${numeroRapport || ''}` :
                                isEditing ? `Modifier le PV ${numeroRapport || ''}` :
                                    'Nouveau Rapport d\'Expertise'}
                        </h1>
                        {sinistre?.numero_sinistre && (
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px', fontFamily: 'monospace', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: '4px' }}>
                                Réf. Sinistre: {sinistre.numero_sinistre}
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {isReadOnly && (
                            <button className="btn btn-primary btn-sm" onClick={() => navigate(`/rapports/${id}/modifier`)}>
                                <Edit3 size={14} /> Modifier
                            </button>
                        )}
                        {!isReadOnly && (
                            <button className="btn btn-outline btn-sm" onClick={() => handleSubmit('brouillon')} disabled={saving}>
                                <Save size={14} /> Brouillon
                            </button>
                        )}
                        {(rapportId || id) && (
                            <button className="btn btn-pdf btn-sm" onClick={handleGeneratePDF}>
                                <FileDown size={14} /> Générer PDF
                            </button>
                        )}
                        {(rapportId || id) && form.statut === 'finalise' && (
                            <button className="btn btn-primary btn-sm" onClick={handleSendEmail} style={{ background: 'var(--primary-800)', boxShadow: '0 4px 12px rgba(10, 22, 40, 0.2)' }}>
                                <MailCheck size={14} /> Envoyer au Mandataire
                            </button>
                        )}
                        {!isReadOnly && (
                            <button className="btn btn-primary btn-sm" onClick={() => handleSubmit('finalise')} disabled={saving}>
                                {saving ? 'Sauvegarde...' : <><Save size={14} /> Finaliser</>}
                            </button>
                        )}
                    </div>
                </div>

                {/* Sinistre Selection (when creating new) */}
                {!sinistre && !isEditing && (
                    <div className="premium-selection-card" style={{
                        background: 'linear-gradient(135deg, white 0%, var(--surface-1) 100%)',
                        border: '1px solid var(--primary-100)',
                        borderRadius: '30px',
                        padding: '60px 40px',
                        textAlign: 'center',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.05)',
                        position: 'relative',
                        overflow: 'hidden',
                        marginBottom: '30px'
                    }}>
                        {/* Decorative background elements */}
                        <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '250px', height: '250px', background: 'radial-gradient(circle, var(--primary-50) 0%, transparent 70%)', borderRadius: '50%', zIndex: 0 }} />
                        <div style={{ position: 'absolute', bottom: '-80px', left: '-80px', width: '200px', height: '200px', background: 'radial-gradient(circle, var(--gold-50) 0%, transparent 70%)', borderRadius: '50%', zIndex: 0 }} />

                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <div
                                onClick={handleOpenModal}
                                style={{
                                    background: 'linear-gradient(135deg, var(--primary-500), var(--primary-700))',
                                    color: 'white',
                                    width: '84px',
                                    height: '84px',
                                    borderRadius: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 24px',
                                    transform: 'rotate(-5deg)',
                                    boxShadow: '0 10px 30px rgba(37, 99, 168, 0.2)',
                                    cursor: 'pointer',
                                    animation: 'premiumFloat 4s ease-in-out infinite, premiumPulse 3s ease-in-out infinite',
                                    transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                    zIndex: 1
                                }}
                                className="plus-btn-animated"
                            >
                                <div className="slow-rotate" style={{ position: 'absolute', width: '100%', height: '100%', border: '2px solid rgba(255,255,255,0.1)', borderRadius: '24px', pointerEvents: 'none' }} />
                                <Plus size={40} strokeWidth={2.5} />
                            </div>
                            <h2 style={{ fontSize: '28px', fontWeight: '900', background: 'linear-gradient(to right, var(--primary-900), var(--primary-600))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '12px' }}>
                                Commencer un Nouveau PV/Rapport
                            </h2>
                            <p style={{ fontSize: '16px', color: 'var(--text-muted)', marginBottom: '32px', maxWidth: '450px', margin: '0 auto 32px', lineHeight: 1.6 }}>
                                Liez ce rapport à un dossier de sinistre existant pour importer automatiquement toutes les données du véhicule et de l'assurance.
                            </p>
                            <button
                                className="btn btn-primary"
                                onClick={handleOpenModal}
                                style={{
                                    padding: '16px 40px',
                                    fontSize: '16px',
                                    fontWeight: '800',
                                    borderRadius: '16px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    boxShadow: '0 10px 20px var(--primary-200)',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                <Search size={20} /> Sélectionner un Dossier
                            </button>
                        </div>
                    </div>
                )}

                {/* Sinistre Reference - Comprehensive View */}
                {sinistre && (
                    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '24px', marginBottom: '24px', boxShadow: 'var(--shadow-sm)', position: 'relative' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', borderBottom: '1px solid var(--surface-3)', paddingBottom: '15px' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <h2 style={{ fontSize: '18px', fontWeight: '800', color: 'var(--primary-800)', margin: 0 }}>
                                        {sinistre.reference_interne
                                            ? <>{sinistre.reference_interne} <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-muted)' }}>— N° Assurance: {sinistre.numero_sinistre || '—'}</span></>
                                            : sinistre.numero_sinistre || '—'
                                        }
                                    </h2>
                                    {sinistre.assurance_logo && (
                                        <img
                                            src={`/assurances_logo/${sinistre.assurance_logo}`}
                                            alt="Logo Assurance"
                                            style={{ height: '90px', maxWidth: '200px', objectFit: 'contain' }}
                                        />
                                    )}
                                </div>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Date de l'accident: <b>{sinistre.date_sinistre ? new Date(sinistre.date_sinistre).toLocaleDateString('fr-DZ') : '—'}</b> | Statut: <span className="badge badge-blue" style={{ fontSize: '10px' }}>{sinistre.statut}</span></div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                {!isEditing && !isReadOnly && (
                                    <button className="btn btn-outline btn-sm" onClick={handleUnlinkSinistre} style={{ color: 'var(--danger)', borderColor: 'var(--danger-200)' }}>
                                        <X size={14} /> Détacher
                                    </button>
                                )}
                                {!isReadOnly && (
                                    <button
                                        className="btn btn-primary"
                                        type="button"
                                        onClick={handleEditSinistreOpen}
                                        style={{ background: 'var(--primary-600)', boxShadow: 'var(--shadow-sm)' }}
                                    >
                                        <Edit3 size={16} /> ÉDITER LE DOSSIER
                                    </button>
                                )}
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '30px' }}>
                            {/* Colonne 1: Assuré & Assurance */}
                            <div style={{ borderRight: '1px solid var(--surface-3)', paddingRight: '20px' }}>
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)', fontWeight: '800', textTransform: 'uppercase', marginBottom: '6px' }}>Assuré</label>
                                    <div style={{ fontSize: '13px', fontWeight: '800', color: 'var(--primary-800)' }}>{sinistre.nom_assure}</div>
                                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}><b>Police:</b> {sinistre.numero_police || '—'}</div>
                                </div>
                                <div style={{ paddingTop: '10px', borderTop: '1px dashed var(--border)' }}>
                                    <label style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)', fontWeight: '800', textTransform: 'uppercase', marginBottom: '6px' }}>Assurance</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {sinistre.assurance_logo && (
                                            <img
                                                src={`/assurances_logo/${sinistre.assurance_logo}`}
                                                alt="Logo Assurance"
                                                style={{ height: '40px', maxWidth: '120px', objectFit: 'contain' }}
                                            />
                                        )}
                                        <div>
                                            <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--primary-700)' }}>{sinistre.nom_assurance || 'Non spécifiée'}</div>
                                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}><b>Code:</b> {sinistre.code_assurance || '—'} | {sinistre.ville_assurance || ''}</div>
                                            {sinistre.assurance_email && (
                                                <div style={{ fontSize: '11px', color: 'var(--primary-600)', fontWeight: '600', marginTop: '2px' }}>
                                                    {sinistre.assurance_email}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Colonne 2: Véhicule */}
                            <div style={{ borderRight: '1px solid var(--surface-3)', paddingRight: '20px' }}>
                                <label style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)', fontWeight: '800', textTransform: 'uppercase', marginBottom: '4px' }}>Véhicule</label>
                                <div style={{ position: 'relative' }}>
                                    {sinistre.marque_logo && (
                                        <img
                                            src={`${BASE_URL}/car_logos/${sinistre.marque_logo}`}
                                            alt="Logo"
                                            style={{ position: 'absolute', right: 0, top: 0, height: '120px', maxWidth: '140px', objectFit: 'contain' }}
                                        />
                                    )}
                                    <div style={{ fontSize: '18px', fontWeight: '900', color: 'var(--primary)', marginBottom: '6px', paddingRight: sinistre.marque_logo ? '150px' : '0' }}>{sinistre.marque_vehicule} {sinistre.modele_vehicule}</div>
                                    <Plate value={sinistre.immatriculation} style={{ marginBottom: '5px' }} />
                                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '3px' }}><b>Année:</b> {sinistre.annee_vehicule || '—'} | <b>Type:</b> {sinistre.type_vehicule || '—'}</div>
                                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}><b>Genre:</b> {sinistre.genre || '—'} | <b>Puiss:</b> {sinistre.puissance || '—'} CV</div>
                                </div>
                            </div>

                            {/* Colonne 3: Technique */}
                            <div style={{ borderRight: '1px solid var(--surface-3)', paddingRight: '20px' }}>
                                <label style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)', fontWeight: '800', textTransform: 'uppercase', marginBottom: '6px' }}>Caractéristiques Techniques</label>
                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}><b>N° Châssis:</b> <span style={{ fontFamily: 'monospace', fontWeight: '700' }}>{sinistre.numero_serie || '—'}</span></div>
                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}><b>Énergie:</b> {sinistre.energie || '—'}</div>
                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}><b>Carrosserie:</b> {sinistre.carrosserie || '—'}</div>
                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}><b>Couleur:</b> {sinistre.couleur || '—'}</div>
                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}><b>État:</b> <span style={{ color: 'var(--primary-600)', fontWeight: '800' }}>{sinistre.etat_vehicule || '—'}</span></div>
                            </div>

                            {/* Colonne 4: Mission & Tiers */}
                            <div>
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)', fontWeight: '800', textTransform: 'uppercase', marginBottom: '6px' }}>Tiers Impliqué</label>
                                    <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--danger)' }}>{sinistre.nom_tiers || 'Aucun tiers'}</div>
                                </div>
                                <div style={{ paddingTop: '10px', borderTop: '1px dashed var(--border)' }}>
                                    <label style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)', fontWeight: '800', textTransform: 'uppercase', marginBottom: '6px' }}>Mission / Lieux</label>
                                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}><b>Lieu Visite:</b> {sinistre.lieu_visite || '—'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Sinistre Modal - Ultra-Wide 3-Column Layout */}
                {showEditSinistreModal && (
                    <div className="modal-overlay glass-modal">
                        <div className="modal" style={{ maxWidth: '1700px', width: '96vw', height: '90vh', borderRadius: '24px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}>
                            {/* Header - Vibrant Artistic Design */}
                            <div className="modal-header" style={{
                                padding: '30px 40px',
                                background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
                                borderBottom: 'none',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                {/* Artistic Background Decor */}
                                <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }}></div>
                                <div style={{ position: 'absolute', bottom: '-80px', left: '100px', width: '150px', height: '150px', borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }}></div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '25px', position: 'relative', zIndex: 1 }}>
                                    <div style={{
                                        width: '60px',
                                        height: '60px',
                                        background: 'rgba(255,255,255,0.15)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: '18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                                        border: '1px solid rgba(255,255,255,0.2)'
                                    }}>
                                        <Edit3 size={28} style={{ color: 'white' }} />
                                    </div>
                                    <div>
                                        <h2 className="modal-title" style={{ fontSize: '24px', fontWeight: '900', color: 'white', marginBottom: '4px', letterSpacing: '-0.5px' }}>Expertise Technique</h2>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <span style={{ fontSize: '11px', background: 'white', color: 'var(--primary)', padding: '3px 12px', borderRadius: '20px', fontWeight: '800', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                                                {editForm.reference_interne || editForm.numero_sinistre || 'SANS-RÉF'}
                                            </span>
                                            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>
                                                Édition globale des caractéristiques et données du dossier
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <button className="modal-close" onClick={() => setShowEditSinistreModal(false)} style={{
                                    width: '45px',
                                    height: '45px',
                                    background: 'rgba(255,255,255,0.1)',
                                    backdropFilter: 'blur(8px)',
                                    borderRadius: '50%',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    color: 'white'
                                }}><X size={22} /></button>
                            </div>

                            <div className="modal-body" style={{ flex: 1, overflowY: 'auto', background: 'var(--surface-2)', padding: '30px 40px' }}>
                                <div className="premium-grid-layout">

                                    {/* Column 1: Identity */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                                        <div className="premium-card">
                                            <div className="premium-header">
                                                <User size={16} />
                                                <span>Identité de l'Assuré</span>
                                                {editForm.assurance_logo && (
                                                    <div style={{ marginLeft: 'auto', background: '#fff', padding: '5px 15px', borderRadius: '10px', height: '60px', display: 'flex', alignItems: 'center', border: '1px solid rgba(0,0,0,0.1)', boxShadow: 'var(--shadow-sm)' }}>
                                                        <img
                                                            src={`/assurances_logo/${editForm.assurance_logo}`}
                                                            alt="Logo"
                                                            style={{ height: '45px', objectFit: 'contain' }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                                <div className="premium-input-group">
                                                    <label className="form-label">N° Sin.Assur</label>
                                                    <input type="text" className="form-control" style={{ fontWeight: 800, fontSize: '15px', color: 'var(--primary)' }} value={editForm.numero_sinistre || ''} onChange={e => setEditField('numero_sinistre', e.target.value)} />
                                                </div>
                                                <div className="premium-input-group">
                                                    <label className="form-label">Nom Complet de l'Assuré</label>
                                                    <input type="text" className="form-control" value={editForm.nom_assure || ''} onChange={e => setEditField('nom_assure', e.target.value)} />
                                                </div>
                                                <div className="premium-input-group">
                                                    <label className="form-label">Date de l'Accident</label>
                                                    <input type="date" className="form-control" value={editForm.date_sinistre || ''} onChange={e => setEditField('date_sinistre', e.target.value)} />
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    {/* Column 2: Insurance & Location */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                                        <div className="premium-card">
                                            <div className="premium-header">
                                                <Shield size={16} />
                                                <span>Mission & Assurance</span>
                                            </div>
                                            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                                <div className="premium-input-group">
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                        <label className="form-label" style={{ marginBottom: 0 }}>Compagnie d'Assurance</label>
                                                        <button
                                                            type="button"
                                                            className="btn btn-ghost"
                                                            onClick={() => setShowAddAssuranceModal(true)}
                                                            style={{ padding: '0 4px', height: '24px', display: 'flex', alignItems: 'center', color: 'var(--primary-600)', background: 'var(--primary-50)', borderRadius: '4px' }}
                                                            title="Nouvelle Assurance"
                                                        >
                                                            <Plus size={14} style={{ marginRight: '4px' }} />
                                                            <span style={{ fontSize: '11px', fontWeight: 700 }}>Nouveau</span>
                                                        </button>
                                                    </div>
                                                    <select className="form-control" value={editForm.nom_assurance || ''} onChange={handleAssuranceSelectInModal}>
                                                        <option value="">Sélectionner une compagnie...</option>
                                                        {[...new Set(assurances.filter(a => a && a.name).map(a => a.name))].sort().map(name => (
                                                            <option key={name} value={name}>{name}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {editForm.assurance_logo && (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '10px' }}>
                                                        <img
                                                            src={`/assurances_logo/${editForm.assurance_logo}`}
                                                            alt="Logo Previsualisation"
                                                            style={{ height: '80px', maxWidth: '100%', objectFit: 'contain' }}
                                                        />
                                                    </div>
                                                )}

                                                {Array.isArray(assurances) && assurances.filter(a => a && a.name === editForm.nom_assurance).length > 1 && (
                                                    <div className="premium-input-group">
                                                        <label className="form-label" style={{ color: 'var(--primary-600)', fontWeight: 700 }}>Choisir l'Agence / Succursale</label>
                                                        <select
                                                            className="form-control"
                                                            style={{ border: '2px solid var(--primary-200)', background: 'var(--primary-50)' }}
                                                            onChange={(e) => {
                                                                const ass = assurances.find(a => a.id === parseInt(e.target.value));
                                                                if (ass) {
                                                                    setEditForm(prev => ({ ...prev, code_assurance: ass.code, ville_assurance: ass.city, assurance_logo: ass.logo }));
                                                                }
                                                            }}
                                                        >
                                                            <option value="">-- Sélectionner l'agence --</option>
                                                            {assurances.filter(a => a && a.name === editForm.nom_assurance).map(a => (
                                                                <option key={a.id} value={a.id}>{a.city} (Code: {a.code})</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}

                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">N° de Police</label>
                                                        <input type="text" className="form-control" value={editForm.numero_police || ''} onChange={e => setEditField('numero_police', e.target.value)} />
                                                    </div>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">Code Assurance</label>
                                                        <input type="text" className="form-control" value={editForm.code_assurance || ''} onChange={e => setEditField('code_assurance', e.target.value)} />
                                                    </div>
                                                </div>
                                                <div className="premium-input-group">
                                                    <label className="form-label">Ville / Agence</label>
                                                    <input type="text" className="form-control" value={editForm.ville_assurance || ''} onChange={e => setEditField('ville_assurance', e.target.value)} />
                                                </div>
                                                <div className="premium-input-group">
                                                    <label className="form-label">Lieu de la Visite Technique</label>
                                                    <div style={{ position: 'relative' }}>
                                                        <MapPin size={14} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                                        <input type="text" className="form-control" style={{ paddingRight: '35px' }} value={editForm.lieu_visite || ''} onChange={e => setEditField('lieu_visite', e.target.value)} />
                                                    </div>
                                                </div>
                                                <div className="premium-input-group">
                                                    <label className="form-label">Nom du Tiers</label>
                                                    <input type="text" className="form-control" value={editForm.nom_tiers || ''} onChange={e => setEditField('nom_tiers', e.target.value)} placeholder="Nom du tiers..." />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Column 3: Technical Specs (Wider content) */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                                        <div className="premium-card">
                                            <div className="premium-header">
                                                <Car size={16} />
                                                <span>Spécifications du Véhicule</span>
                                                {editForm.marque_logo && (
                                                    <img
                                                        src={`${BASE_URL}/car_logos/${editForm.marque_logo}`}
                                                        alt="Marque"
                                                        style={{ marginLeft: 'auto', height: '110px', maxWidth: '140px', objectFit: 'contain' }}
                                                    />
                                                )}
                                            </div>
                                            <div style={{ padding: '20px' }}>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">Marque</label>
                                                        <input list="modalMarquesList" className="form-control" style={{ fontWeight: 600 }} value={editForm.marque_vehicule || ''} onChange={e => handleMarqueChangeInModal(e.target.value)} />
                                                        <datalist id="modalMarquesList">{Array.isArray(marques) && marques.map(m => <option key={m.id} value={m.name} />)}</datalist>
                                                    </div>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">Modèle / Version</label>
                                                        <input list="modalModelesList" className="form-control" style={{ fontWeight: 600 }} value={editForm.modele_vehicule || ''} onChange={e => handleModeleChangeInModal(e.target.value)} />
                                                        <datalist id="modalModelesList">{Array.isArray(modeles) && modeles.map(m => <option key={m.id} value={m.name} />)}</datalist>
                                                    </div>
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '12px', marginBottom: '15px' }}>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">Immatriculation</label>
                                                        <input type="text" className="form-control" style={{ textTransform: 'uppercase', fontWeight: 700, fontFamily: 'monospace', fontSize: '14px', background: 'var(--surface-3)' }} value={editForm.immatriculation || ''} onChange={e => setEditField('immatriculation', e.target.value)} />
                                                    </div>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">Année M.E.C</label>
                                                        <input list="modalYearsList" type="number" className="form-control" value={editForm.annee_vehicule || ''} onChange={e => setEditField('annee_vehicule', e.target.value)} />
                                                        <datalist id="modalYearsList">{Array.isArray(modelYears) && modelYears.map(y => <option key={y} value={y} />)}</datalist>
                                                    </div>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">Puiss. (CV)</label>
                                                        <input type="number" className="form-control" value={editForm.puissance || ''} onChange={e => setEditField('puissance', e.target.value)} />
                                                    </div>
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '12px', marginBottom: '15px' }}>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">Type</label>
                                                        <input type="text" className="form-control" value={editForm.type_vehicule || ''} onChange={e => setEditField('type_vehicule', e.target.value)} />
                                                    </div>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">Genre</label>
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <select className="form-control" value={editForm.genre || ''} onChange={e => setEditField('genre', e.target.value)}>
                                                                <option value="">Sélectionner...</option>
                                                                {Array.isArray(genres) && genres.map(g => <option key={g.id} value={g.genre}>{g.genre}</option>)}
                                                            </select>
                                                            <button type="button" className="btn btn-ghost" onClick={() => setQuickAdd({ type: 'genre', value: '' })} style={{ padding: '0 8px', background: 'var(--surface-3)' }}><Plus size={16} /></button>
                                                        </div>
                                                    </div>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">Énergie</label>
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <select className="form-control" value={editForm.energie || ''} onChange={e => setEditField('energie', e.target.value)}>
                                                                <option value="">Diesel/Ess...</option>
                                                                {Array.isArray(energies) && energies.map(en => <option key={en.id} value={en.energie}>{en.energie}</option>)}
                                                            </select>
                                                            <button type="button" className="btn btn-ghost" onClick={() => setQuickAdd({ type: 'energie', value: '' })} style={{ padding: '0 8px', background: 'var(--surface-3)' }}><Plus size={16} /></button>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '15px' }}>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">Carrosserie</label>
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <select className="form-control" value={editForm.carrosserie || ''} onChange={e => setEditField('carrosserie', e.target.value)}>
                                                                <option value="">Sélectionner...</option>
                                                                {Array.isArray(carosseries) && carosseries.map(c => <option key={c.id} value={c.carosserie}>{c.carosserie}</option>)}
                                                            </select>
                                                            <button type="button" className="btn btn-ghost" onClick={() => setQuickAdd({ type: 'carrosserie', value: '' })} style={{ padding: '0 8px', background: 'var(--surface-3)' }}><Plus size={16} /></button>
                                                        </div>
                                                    </div>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">État véhicule</label>
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <select className="form-control" value={editForm.etat_vehicule || ''} onChange={e => setEditField('etat_vehicule', e.target.value)}>
                                                                <option value="">Sélectionner...</option>
                                                                {Array.isArray(estados) && estados.map(st => <option key={st.id} value={st.estado}>{st.estado}</option>)}
                                                            </select>
                                                            <button type="button" className="btn btn-ghost" onClick={() => setQuickAdd({ type: 'estado', value: '' })} style={{ padding: '0 8px', background: 'var(--surface-3)' }}><Plus size={16} /></button>
                                                        </div>
                                                    </div>
                                                    <div className="premium-input-group">
                                                        <label className="form-label">Teinte / Couleur</label>
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <select className="form-control" value={editForm.couleur || ''} onChange={e => setEditField('couleur', e.target.value)}>
                                                                <option value="">Teinte...</option>
                                                                {Array.isArray(colors) && colors.map(c => <option key={c.id} value={c.color}>{c.color}</option>)}
                                                            </select>
                                                            <button type="button" className="btn btn-ghost" onClick={() => setQuickAdd({ type: 'color', value: '' })} style={{ padding: '0 8px', background: 'var(--surface-3)' }}><Plus size={16} /></button>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="premium-input-group">
                                                    <label className="form-label">N° de Série (Châssis)</label>
                                                    <input type="text" className="form-control" style={{ fontFamily: 'monospace', letterSpacing: '1px', fontSize: '14px', background: 'var(--surface-3)' }} value={editForm.numero_serie || ''} onChange={e => setEditField('numero_serie', e.target.value)} placeholder="VIN NUMBER..." />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                            {/* Footer */}
                            <div className="modal-footer" style={{ padding: '24px 40px', background: 'var(--surface-1)', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ width: '8px', height: '8px', background: 'var(--success)', borderRadius: '50%' }}></div>
                                    Système prêt pour la mise à jour
                                </div>
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <button className="btn btn-ghost btn-lg" style={{ borderRadius: '14px', padding: '10px 30px' }} onClick={() => setShowEditSinistreModal(false)}>Annuler</button>
                                    <button className="btn btn-primary btn-lg" onClick={handleEditSinistreSave} disabled={updatingSinistre}
                                        style={{
                                            borderRadius: '14px',
                                            minWidth: '240px',
                                            background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
                                            boxShadow: '0 10px 20px rgba(var(--primary-rgb), 0.3)',
                                            border: 'none',
                                            padding: '10px 25px'
                                        }}>
                                        {updatingSinistre ? 'Synchronisation...' : 'Enregistrer les Modifications'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Quick Add Modal */}
                {quickAdd && (
                    <div className="modal-overlay" style={{ zIndex: 9999 }}>
                        <div className="modal" style={{ maxWidth: '400px' }}>
                            <div className="modal-header">
                                <h3 className="modal-title">
                                    {quickAdd.type === 'genre' && 'Ajouter un nouveau Genre'}
                                    {quickAdd.type === 'carrosserie' && 'Ajouter une nouvelle Carrosserie'}
                                    {quickAdd.type === 'color' && 'Ajouter une nouvelle Couleur'}
                                    {quickAdd.type === 'energie' && 'Ajouter une nouvelle Énergie'}
                                    {quickAdd.type === 'estado' && 'Ajouter un nouvel État'}
                                </h3>
                                <button className="modal-close" onClick={() => setQuickAdd(null)}><X size={18} /></button>
                            </div>
                            <div className="modal-body">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Valeur"
                                    value={quickAdd.value}
                                    onChange={e => setQuickAdd({ ...quickAdd, value: e.target.value })}
                                    autoFocus
                                />
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-ghost" onClick={() => setQuickAdd(null)}>Annuler</button>
                                <button className="btn btn-primary" onClick={handleQuickAdd}>Ajouter</button>
                            </div>
                        </div>
                    </div>
                )}



                {/* Add Assurance Modal */}
                {showAddAssuranceModal && (
                    <div className="modal-overlay" style={{ zIndex: 10000 }}>
                        <div className="modal" style={{ maxWidth: '450px', borderRadius: '16px', overflow: 'hidden' }}>
                            <div className="modal-header" style={{ background: 'var(--primary-600)', color: 'white', padding: '15px 20px' }}>
                                <h3 className="modal-title" style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Shield size={18} />
                                    Nouvelle Fiche Assurance
                                </h3>
                                <button className="modal-close" onClick={() => setShowAddAssuranceModal(false)} style={{ color: 'white', opacity: 0.8 }}><X size={18} /></button>
                            </div>
                            <div className="modal-body" style={{ padding: '25px', background: 'white' }}>
                                <div style={{ display: 'grid', gap: '15px' }}>
                                    <div className="premium-input-group">
                                        <label className="form-label" style={{ color: 'var(--primary-700)', fontWeight: 700 }}>Nom de la Compagnie</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Ex: AXA, RMA..."
                                            value={newAssurance.name}
                                            onChange={e => setNewAssurance({ ...newAssurance, name: e.target.value })}
                                            autoFocus
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                        <div className="premium-input-group">
                                            <label className="form-label">Code Assurance</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Code"
                                                value={newAssurance.code}
                                                onChange={e => setNewAssurance({ ...newAssurance, code: e.target.value })}
                                            />
                                        </div>
                                        <div className="premium-input-group">
                                            <label className="form-label">Ville / Agence</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Ville"
                                                value={newAssurance.city}
                                                onChange={e => setNewAssurance({ ...newAssurance, city: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="premium-input-group">
                                        <label className="form-label">Téléphone / Agent</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="N° de Téléphone"
                                            value={newAssurance.phone}
                                            onChange={e => setNewAssurance({ ...newAssurance, phone: e.target.value })}
                                        />
                                    </div>
                                    <div className="premium-input-group">
                                        <label className="form-label">Email de Contact</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            placeholder="Ex: rapport@companie.dz"
                                            value={newAssurance.email}
                                            onChange={e => setNewAssurance({ ...newAssurance, email: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer" style={{ padding: '15px 20px', background: 'var(--surface-2)', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button className="btn btn-ghost" onClick={() => setShowAddAssuranceModal(false)} disabled={savingAssurance}>Annuler</button>
                                <button className="btn btn-primary" onClick={handleSaveNewAssurance} disabled={savingAssurance} style={{ minWidth: '120px' }}>
                                    {savingAssurance ? 'Enregistrement...' : 'Sauvegarder'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <div className="grid-2" style={{ gap: '16px', marginBottom: '16px' }}>
                    {/* Left column */}
                    <div>
                        {/* Date & Statut */}
                        <div className="card" style={{ marginBottom: '16px' }}>
                            <div className="form-section-title">📋 Informations du Rapport</div>
                            <div className="form-grid form-grid-2">
                                <div className="form-group">
                                    <label className="form-label">Date du Rapport</label>
                                    <DatePicker
                                        selected={form.date_rapport ? new Date(form.date_rapport) : null}
                                        onChange={(date) => setForm(p => ({ ...p, date_rapport: date ? date.toISOString().split('T')[0] : '' }))}
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        locale="fr"
                                        placeholderText="jj/mm/aaaa"
                                        disabled={isReadOnly}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Statut</label>
                                    <select className="form-control" value={form.statut} disabled={isReadOnly} onChange={e => setForm(p => ({ ...p, statut: e.target.value }))}>
                                        <option value="brouillon">Brouillon</option>
                                        <option value="finalise">Finalisé</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Nombre de Photos</label>
                                    <input type="number" className="form-control" min="0"
                                        value={actualPhotoCount || form.nb_photos || 0}
                                        disabled style={{ background: 'var(--surface-2)', cursor: 'not-allowed' }} />
                                </div>
                            </div>
                        </div>

                        {/* Constatations Techniques Originales avec bouton IA */}
                        <div className="card" style={{ marginBottom: '16px' }}>
                            <div className="form-section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'nowrap', gap: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flexShrink: 1 }}>
                                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>🔍 Constatations Techniques</span>
                                    {!!form.ia_generated && (
                                        <span className="badge-ia-cool" style={{
                                            fontSize: '9px', fontWeight: '800', padding: '2px 6px',
                                            color: 'white', borderRadius: '4px', letterSpacing: '0.5px',
                                            whiteSpace: 'nowrap', flexShrink: 0
                                        }}>
                                            ✨ IA
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                                    {!isReadOnly && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-ia"
                                            title="Analyser les photos avec l'IA"
                                            style={{
                                                borderRadius: '6px',
                                                fontWeight: '700',
                                                fontSize: '11px',
                                                padding: '6px 10px',
                                                cursor: analysingIA ? 'not-allowed' : 'pointer',
                                                display: 'flex', alignItems: 'center', gap: '4px',
                                                whiteSpace: 'nowrap',
                                                ...(analysingIA ? { opacity: 0.7, cursor: 'wait' } : {})
                                            }}
                                            disabled={analysingIA}
                                            onClick={handleAnalyseIA}
                                        >
                                            {analysingIA ? (
                                                <><div className="spinner-ia" /> ...</>
                                            ) : (
                                                <><Sparkles size={12} /> Analyse IA</>
                                            )}
                                        </button>
                                    )}
                                    {!isReadOnly && (
                                        <DictationButton onTranscript={(text) => setForm(p => ({ ...p, constatations: (p.constatations ? p.constatations + ' ' : '') + text }))} />
                                    )}
                                </div>
                            </div>

                            {!!form.ia_generated && !!form.ia_generated_at && (
                                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '8px', fontStyle: 'italic' }}>
                                    Générées le {form.ia_generated_at ? new Date(form.ia_generated_at).toLocaleString('fr-DZ') : '—'} — Modifiable
                                </div>
                            )}

                            <textarea
                                className="form-control"
                                rows={7}
                                placeholder="Cliquez sur 'Analyser avec IA' pour generer les constatations à partir des photos, ou dictée vocale..."
                                value={form.constatations}
                                disabled={isReadOnly}
                                onChange={e => setForm(p => ({ ...p, constatations: e.target.value }))}
                                style={form.ia_generated ? { borderColor: '#a5b4fc', background: 'rgba(99,102,241,0.02)' } : {}}
                            />

                            {!isReadOnly && (
                                <TemplateSuggester
                                    field="constatations"
                                    value={form.constatations}
                                    onSelect={(selected) => setForm(p => ({ ...p, constatations: selected }))}
                                />
                            )}
                        </div>

                        {/* Valeur vénale & Vétusté */}
                        <div className="card">
                            <div className="form-section-title">📊 Évaluation Complémentaire</div>
                            <div className="form-grid form-grid-2">
                                <div className="form-group">
                                    <label className="form-label">Valeur Vénale (DA)</label>
                                    <input type="text" className="form-control" value={form.valeur_venale}
                                        disabled={isReadOnly}
                                        onBlur={() => handleBlur('valeur_venale')}
                                        onChange={e => setForm(p => ({ ...p, valeur_venale: e.target.value }))} placeholder="0" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Vétusté (%)</label>
                                    <input type="number" className="form-control" min="0" max="100" value={form.vetuste}
                                        disabled={isReadOnly}
                                        onChange={e => setForm(p => ({ ...p, vetuste: e.target.value }))} placeholder="0" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Montant Vétusté (DA)</label>
                                    <input type="text" className="form-control" value={form.vetuste_montant}
                                        disabled={isReadOnly}
                                        onBlur={() => handleBlur('vetuste_montant')}
                                        onChange={e => setForm(p => ({ ...p, vetuste_montant: e.target.value }))} placeholder="0" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Jours d'Immobilisation</label>
                                    <input type="number" className="form-control" min="0" value={form.jours_immobilisation}
                                        disabled={isReadOnly}
                                        onChange={e => setForm(p => ({ ...p, jours_immobilisation: e.target.value }))} placeholder="0" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right column - Totaux */}
                    <div>
                        {/* Main d'oeuvre & Peinture */}
                        <div className="card" style={{ marginBottom: '16px' }}>
                            <div className="form-section-title">💰 Évaluation des Coûts</div>
                            <div className="form-group">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                    <label className="form-label" style={{ margin: 0 }}>Descriptif Main d'Œuvre</label>
                                    {!isReadOnly && (
                                        <DictationButton
                                            onTranscript={(text) => setForm(p => ({ ...p, main_oeuvre_txt: p.main_oeuvre_txt ? p.main_oeuvre_txt + ' ' + text : text }))}
                                        />
                                    )}
                                </div>
                                <textarea className="form-control" rows={2} value={form.main_oeuvre_txt}
                                    disabled={isReadOnly}
                                    onChange={e => setForm(p => ({ ...p, main_oeuvre_txt: e.target.value }))}
                                    placeholder="Ex: Remise en état du capot moteur, application de peinture..." />
                                {!isReadOnly && (
                                    <TemplateSuggester
                                        field="main_oeuvre_txt"
                                        value={form.main_oeuvre_txt}
                                        onSelect={(selected) => setForm(p => ({ ...p, main_oeuvre_txt: selected }))}
                                    />
                                )}
                            </div>
                            <div className="form-group">
                                <label className="form-label">Montant Main d'Œuvre (DA)</label>
                                <div style={{ position: 'relative' }}>
                                    <input type="text" className="form-control" value={form.main_oeuvre}
                                        disabled={true}
                                        style={{ background: 'var(--surface-2)', cursor: 'not-allowed', fontWeight: '700' }}
                                        onBlur={() => handleBlur('main_oeuvre')}
                                        onChange={e => setForm(p => ({ ...p, main_oeuvre: e.target.value }))} placeholder="0,00" />
                                    {form.jours_immobilisation > 0 && (
                                        <div style={{ fontSize: '10px', color: 'var(--primary-600)', marginTop: '4px', fontWeight: '600' }}>
                                            Calculé: {form.jours_immobilisation * 8}h x {(sinistre?.genre || '').toLowerCase() === 'camion' ? (systemParams.taux_horaire_mo_camion || 300) : (systemParams.taux_horaire_mo || 250)} DA/h
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Peinture & Matière (DA)</label>
                                <input type="text" className="form-control" value={form.peinture}
                                    disabled={isReadOnly}
                                    onBlur={() => handleBlur('peinture')}
                                    onChange={e => setForm(p => ({ ...p, peinture: e.target.value }))} placeholder="0,00" />
                            </div>
                            {/* Live Totals Widget */}
                            <div style={{ background: 'var(--primary-900)', borderRadius: 'var(--radius)', padding: '14px', marginTop: '12px' }}>
                                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', marginBottom: '8px', textTransform: 'uppercase', fontWeight: '700' }}>Récapitulatif</div>
                                {[
                                    { label: 'Main d\'Œuvre', value: parseMontant(form.main_oeuvre) || 0 },
                                    { label: 'Peinture', value: parseMontant(form.peinture) || 0 },
                                    { label: 'Pièces / Fournitures', value: totals.pieces },
                                    { label: 'Vétusté (-)', value: -(parseMontant(form.vetuste_montant) || 0), isDeduction: true }
                                ].map(item => (
                                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>{item.label}</span>
                                        <span style={{ color: item.isDeduction ? 'var(--danger-300)' : 'white', fontWeight: '600', fontSize: '12px' }}>
                                            {formatCurrency(Math.abs(item.value))} {item.isDeduction && item.value !== 0 && '(-)'}
                                        </span>
                                    </div>
                                ))}
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', marginTop: '4px' }}>
                                    <span style={{ color: 'var(--gold-300)', fontSize: '13px', fontWeight: '700' }}>TOTAL GÉNÉRAL</span>
                                    <span style={{ color: 'var(--gold-400)', fontSize: '16px', fontWeight: '800' }}>{formatCurrency(totals.general)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Observations */}
                        <div className="card" style={{ marginTop: '16px' }}>
                            <div className="form-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>📝 Observations Particulières</span>
                                {!isReadOnly && (
                                    <DictationButton
                                        onTranscript={(text) => setForm(p => ({ ...p, observations: p.observations ? p.observations + ' ' + text : text }))}
                                    />
                                )}
                            </div>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={form.observations || ''}
                                disabled={isReadOnly}
                                onChange={e => setForm(p => ({ ...p, observations: e.target.value }))}
                                placeholder="Ajoutez ici des observations particulières qui apparaîtront en bas du rapport..."
                            />
                        </div>


                    </div>
                </div>

                {/* Pièces à Remplacer */}
                <div className="card" style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div className="form-section-title" style={{ margin: 0 }}>🔩 Pièces à Remplacer / Fournitures</div>
                        {!isReadOnly && (
                            <button className="btn btn-outline btn-sm" onClick={addPiece}>
                                <Plus size={13} /> Ajouter une ligne
                            </button>
                        )}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="pieces-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '50%' }}>Désignation</th>
                                    <th style={{ width: '12%', textAlign: 'center' }}>Quantité</th>
                                    <th style={{ width: '18%', textAlign: 'right' }}>Prix Unitaire (DA)</th>
                                    <th style={{ width: '18%', textAlign: 'right' }}>Sous-Total (DA)</th>
                                    <th style={{ width: '7%' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(pieces) && pieces.map((p, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <input
                                                list={`catalog-parts-${idx}`}
                                                className="pieces-input"
                                                placeholder="Désignation de la pieza"
                                                value={p.designation}
                                                disabled={isReadOnly}
                                                onChange={e => handleDesignationChange(idx, e.target.value)}
                                            />
                                            <datalist id={`catalog-parts-${idx}`}>
                                                {availableParts.map((ap, i) => (
                                                    <option key={i} value={ap.name}>
                                                        {ap.model_year ? `${ap.model_year} • ` : ''}{formatCurrency(ap.price)}
                                                    </option>
                                                ))}
                                            </datalist>
                                        </td>
                                        <td>
                                            <input className="pieces-input" type="number" min="0" step="0.01" style={{ textAlign: 'center' }}
                                                disabled={isReadOnly}
                                                value={p.quantite} onChange={e => updatePiece(idx, 'quantite', e.target.value)} />
                                        </td>
                                        <td>
                                            <input className="pieces-input" type="text" style={{ textAlign: 'right' }}
                                                disabled={isReadOnly}
                                                onBlur={() => handlePieceBlur(idx, 'prix_unitaire')}
                                                value={p.prix_unitaire} onChange={e => updatePiece(idx, 'prix_unitaire', e.target.value)} />
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <span className="pieces-subtotal">{formatCurrency(p.sous_total)}</span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {!isReadOnly && (
                                                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '4px' }}
                                                    onClick={() => removePiece(idx)} title="Supprimer">
                                                    <Trash2 size={13} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: 'var(--surface-2)' }}>
                                    <td colSpan={3} style={{ textAlign: 'right', fontWeight: '700', padding: '10px 12px', color: 'var(--text-secondary)' }}>
                                        Total Pièces & Fournitures:
                                    </td>
                                    <td style={{ fontWeight: '800', color: 'var(--primary-700)', fontSize: '14px', padding: '10px 12px', textAlign: 'right' }}>
                                        {formatCurrency(totals.pieces)}
                                    </td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* Photos */}
                <div className="card" style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div className="form-section-title" style={{ margin: 0 }}>📷 Photos ({(sinistre?.photos?.length || 0) + photos.filter(p => !sinistre?.photos?.some(sp => sp.id === p.id)).length})</div>
                        {!isReadOnly && (
                            <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
                                <Camera size={13} /> Ajouter des Photos
                                <input type="file" accept="image/*" multiple capture="environment" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                            </label>
                        )}
                    </div>

                    {uploadProgress > 0 && (
                        <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginBottom: '10px', height: '6px' }}>
                            <div style={{ height: '100%', background: 'var(--primary-400)', width: `${uploadProgress}%`, transition: 'width 0.3s' }} />
                        </div>
                    )}

                    {((sinistre?.photos?.length > 0) || (photos.length > 0)) ? (
                        <div className="photos-grid">
                            {/* Photos du Dossier */}
                            {sinistre?.photos?.map((photo, idx) => (
                                <div key={`s-${idx}`} className="photo-item" style={{ border: '2px solid var(--primary-500)' }} title="Photo du Dossier">
                                    <img src={getPhotoUrl(photo)} alt={photo.original_name || `${idx + 1}`} />
                                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: '10px', padding: '4px', textAlign: 'center' }}>
                                        Photo {idx + 1}
                                    </div>
                                    {!isReadOnly && (
                                        <button className="photo-delete" onClick={() => removeSinistrePhoto(photo)} title="Supprimer du dossier">
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            {/* Photos du Rapport */}
                            {Array.isArray(photos) && photos.filter(p => !sinistre?.photos?.some(sp => sp.id === p.id)).map((photo, idx) => {
                                const photoOrder = (sinistre?.photos?.length || 0) + idx + 1;
                                return (
                                    <div key={`r-${idx}`} className="photo-item" title={`Photo PV ${photoOrder}`}>
                                        <img src={getPhotoUrl(photo)} alt={photo.original_name || `${photoOrder}`} />
                                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.3)', color: 'white', fontSize: '10px', padding: '4px', textAlign: 'center' }}>
                                            Photo {photoOrder}
                                        </div>
                                        {!isReadOnly && (
                                            <button className="photo-delete" onClick={() => removePhoto(photo)} title="Supprimer">
                                                <X size={12} />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="dropzone" onClick={() => document.querySelector('input[type="file"]')?.click()}>
                            <Upload size={32} color="var(--text-muted)" style={{ margin: '0 auto 8px', display: 'block' }} />
                            <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Cliquez pour ajouter des photos ou prenez-les directement depuis votre smartphone</p>
                        </div>
                    )}
                </div>

                {/* Discrete Print Options */}
                {!isReadOnly && (
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'flex-end',
                        gap: '25px', 
                        padding: '10px 20px', 
                        background: 'var(--surface-3)', 
                        borderRadius: '14px', 
                        marginBottom: '20px',
                        border: '1px solid var(--border-2)',
                        boxShadow: 'var(--shadow-sm)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Shield size={14} color="var(--primary-500)" />
                            <span style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Impresión PDF:</span>
                        </div>
                        
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
                            <div 
                                className={`form-toggle ${form.inclure_cachet_signature ? 'active' : ''}`} 
                                onClick={() => {
                                    const newValue = !form.inclure_cachet_signature ? 1 : 0;
                                    setForm(prev => ({ 
                                        ...prev, 
                                        inclure_cachet_signature: newValue,
                                        inclure_cachet_photos: newValue 
                                    }));
                                }}
                                style={{ 
                                    cursor: 'pointer',
                                    width: '38px',
                                    height: '19px',
                                    position: 'relative',
                                    borderRadius: '100px',
                                    background: form.inclure_cachet_signature ? 'var(--gold-500)' : '#cbd5e1',
                                    transition: 'all 0.3s ease',
                                    flexShrink: 0
                                }}
                            >
                                <div className="toggle-dot" style={{
                                    width: '13px',
                                    height: '13px',
                                    background: 'white',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '3px',
                                    left: form.inclure_cachet_signature ? '22px' : '3px',
                                    transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                }}></div>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)' }}>Inclure Cachets & Signature</span>
                        </label>
                    </div>
                )}

                {/* Footer Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button className="btn btn-ghost" onClick={() => navigate(-1)}>Annuler</button>
                    {isReadOnly && (
                        <button className="btn btn-primary" onClick={() => navigate(`/rapports/${id}/modifier`)}>
                            <Edit3 size={15} /> Modifier
                        </button>
                    )}
                    {!isReadOnly && (
                        <button className="btn btn-outline" onClick={() => handleSubmit('brouillon')} disabled={saving}>
                            <Save size={15} /> Brouillon
                        </button>
                    )}
                    {(rapportId || id) && (
                        <button className="btn btn-pdf" onClick={handleGeneratePDF}>
                            <FileDown size={15} /> Générer PDF
                        </button>
                    )}
                    {id && (
                        <button className="btn btn-outline" onClick={handleFacturer}>
                            <ReceiptText size={15} /> Facturer
                        </button>
                    )}
                    {!isReadOnly && (
                        <button className="btn btn-primary" onClick={() => handleSubmit('finalise')} disabled={saving}>
                            {saving ? 'Sauvegarde...' : <><Save size={15} /> Finaliser</>}
                        </button>
                    )}
                    {(id || rapportId) && form.statut === 'finalise' && (
                        <button 
                            className="btn btn-primary" 
                            style={{ background: form.email_sent ? 'var(--success)' : 'var(--primary-700)', border: 'none' }}
                            onClick={handleSendEmail} 
                            disabled={saving}
                        >
                            {form.email_sent ? <><MailCheck size={16} /> Envoyé au Mandataire</> : <><Send size={16} /> Envoyer au Mandataire</>}
                        </button>
                    )}
                </div>
            </div>

            {/* Modal: Sélection Dossier */}
            {/* Modal: Sélection Dossier */}
            {showModal && (
                <div className="modal-overlay" style={{
                    display: 'flex',
                    backdropFilter: 'blur(8px)',
                    background: 'rgba(15, 23, 42, 0.6)'
                }} onClick={() => setShowModal(false)}>
                    <div className="modal-content" style={{
                        width: '1000px',
                        maxWidth: '95vw',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '32px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        boxShadow: '0 50px 100px -20px rgba(0,0,0,0.5)',
                        overflow: 'hidden',
                        background: 'var(--surface-1)'
                    }} onClick={e => e.stopPropagation()}>

                        {/* Premium Header */}
                        <div className="modal-header" style={{
                            background: 'linear-gradient(135deg, var(--primary-700) 0%, var(--primary-900) 100%)',
                            padding: '30px 40px',
                            borderBottom: 'none',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {/* Animated background circle */}
                            <div style={{ position: 'absolute', top: '-50px', right: '-30px', width: '150px', height: '150px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />

                            <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <div>
                                    <h3 style={{ fontSize: '24px', fontWeight: '900', color: 'white', letterSpacing: '-0.5px', margin: 0 }}>
                                        Lier un Dossier
                                    </h3>
                                    <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginTop: '4px' }}>
                                        Recherchez et sélectionnez le dossier de sinistre à expertiser
                                    </p>
                                </div>
                                <button
                                    className="btn-close"
                                    onClick={() => setShowModal(false)}
                                    style={{ background: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', transition: 'all 0.2s' }}
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Search Section */}
                        <div style={{ padding: '30px 40px', background: 'white' }}>
                            <div style={{ position: 'relative', boxShadow: '0 10px 30px rgba(var(--primary-rgb), 0.08)', borderRadius: '20px' }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Nom de l'Assuré, N° de Dossier, Immatriculation..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    autoFocus
                                    style={{
                                        height: '64px',
                                        fontSize: '17px',
                                        paddingLeft: '60px',
                                        borderRadius: '20px',
                                        border: '2px solid var(--surface-3)',
                                        fontWeight: '600',
                                        transition: 'all 0.3s'
                                    }}
                                />
                                <div style={{ position: 'absolute', left: '22px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary-500)' }}>
                                    <Search size={24} strokeWidth={2.5} />
                                </div>
                                {searching && (
                                    <div style={{ position: 'absolute', right: '22px', top: '50%', transform: 'translateY(-50%)' }}>
                                        <div className="spinner-primary" style={{ width: '24px', height: '24px' }} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Results Section */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '0 40px 40px', background: 'white' }}>
                            {searchResults.length > 0 ? (
                                <div style={{ display: 'grid', gap: '16px' }}>
                                    {searchResults.map(s => (
                                        <div
                                            key={s.id}
                                            className="dossier-result-premium"
                                            onClick={() => s.nb_rapports === 0 && handleSelectSinistre(s)}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1.5fr 1fr 1fr 120px',
                                                alignItems: 'center',
                                                padding: '20px 25px',
                                                background: 'var(--surface-1)',
                                                borderRadius: '20px',
                                                border: '1px solid var(--surface-3)',
                                                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                cursor: s.nb_rapports > 0 ? 'default' : 'pointer',
                                                opacity: s.nb_rapports > 0 ? 0.8 : 1
                                            }}
                                        >
                                            {/* Col 1: Assuré & Vehicle */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ fontSize: '16px', fontWeight: '800', color: 'var(--primary-900)' }}>{s.nom_assure || '—'}</div>
                                                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <Car size={14} style={{ color: 'var(--primary-400)' }} />
                                                    {(s.marque_vehicule || '') + ' ' + (s.modele_vehicule || '')}
                                                </div>
                                            </div>

                                            {/* Col 2: Info Numbers */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Dossier & Immat</div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                                                    <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary-600)' }}>{s.numero_sinistre || '—'}</span>
                                                    <Plate value={s.immatriculation} small />
                                                </div>
                                            </div>

                                            {/* Col 3: Date & Status */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Date Sinistre</div>
                                                <div style={{ fontSize: '14px', fontWeight: '600' }}>
                                                    {s.date_sinistre ? new Date(s.date_sinistre).toLocaleDateString('fr-DZ', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                                </div>
                                            </div>

                                            {/* Col 4: Action */}
                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                {s.nb_rapports > 0 ? (
                                                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                                                        <div style={{ background: 'var(--success-50)', color: 'var(--success-700)', padding: '4px 10px', borderRadius: '8px', fontSize: '10px', fontWeight: '900', border: '1px solid var(--success-200)' }}>
                                                            PV EXISTANT
                                                        </div>
                                                        <button
                                                            className="btn btn-ghost btn-xs"
                                                            style={{ fontSize: '11px', fontWeight: '700', color: 'var(--primary-600)', padding: 0 }}
                                                            onClick={(e) => { 
                                                                e.stopPropagation(); 
                                                                setShowModal(false);
                                                                navigate(`/rapports/${s.rapport_id}`); 
                                                            }}
                                                        >
                                                            Voir le rapport
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        onClick={(e) => { e.stopPropagation(); handleSelectSinistre(s); }}
                                                        style={{ borderRadius: '12px', fontWeight: '800', padding: '8px 16px' }}
                                                    >
                                                        Lier
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                                    <div style={{ color: 'var(--surface-4)', marginBottom: '20px' }}>
                                        <Search size={64} style={{ opacity: 0.2 }} />
                                    </div>
                                    {searchQuery.length < 2 ? (
                                        <>
                                            <h4 style={{ fontWeight: '800', color: 'var(--primary-900)' }}>Prêt à rechercher ?</h4>
                                            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Saisissez le nom ou l'immatriculation pour afficher les résultats.</p>
                                        </>
                                    ) : (
                                        <>
                                            <h4 style={{ fontWeight: '800', color: 'var(--primary-900)' }}>Aucun résultat</h4>
                                            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Nous n'avons trouvé aucun dossier correspondant à "{searchQuery}".</p>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <ConfirmModal
                open={showEmailConfirm}
                onCancel={() => setShowEmailConfirm(false)}
                onConfirm={executeSendEmail}
                title="Confirmation d'envoi d'email"
                confirmLabel="Oui, envoyer"
                cancelLabel="Annuler"
                variant="info"
            >
                <div style={{ textAlign: 'center' }}>
                    <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
                        Êtes-vous sûr de vouloir envoyer ce rapport par email au mandataire ?
                    </p>
                    <div style={{ background: 'var(--surface-2)', padding: '15px', borderRadius: '12px', borderLeft: '4px solid var(--primary-500)', textAlign: 'left' }}>
                         <div style={{ marginBottom: '8px' }}>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 800 }}>Compagnie:</span>
                            <div style={{ fontWeight: 700, color: 'var(--primary-800)' }}>{sinistre?.nom_assurance || 'Non spécifiée'}</div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                             <div>
                                <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 800 }}>Code:</span>
                                <div style={{ fontWeight: 700 }}>{sinistre?.code_assurance || '—'}</div>
                            </div>
                            <div>
                                <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 800 }}>Ville:</span>
                                <div style={{ fontWeight: 700 }}>{sinistre?.ville_assurance || '—'}</div>
                            </div>
                        </div>
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed var(--border)' }}>
                             <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 800 }}>Email Destinataire:</span>
                             <div style={{ fontWeight: 700, color: 'var(--primary-600)' }}>{sinistre?.assurance_email || 'Email non configuré'}</div>
                        </div>
                    </div>
                </div>
            </ConfirmModal>

        </Layout >
    );
};

export default RapportForm;
